import { useCallback, useEffect, useRef, useState } from 'react';

import { di } from 'react-magnetic-di';

import { getDocument } from '@atlaskit/browser-apis';
import { EMPTY_CONVERSATION_ID } from '@atlassian/conversation-assistant-store/controllers/chat';
import { useChatSignals } from '@atlassian/conversation-assistant-store/controllers/chat-signals';
import { CHAT_STORE_STATUS } from '@atlassian/conversation-assistant-store/controllers/chat/types';
import { useChatMessages } from '@atlassian/conversation-assistant-store/controllers/chat/use-chat-messages';
import { useConversationActionsStore } from '@atlassian/rovo-conversation-actions/conversation-actions-store';

import { isChatLoading } from '../../../controllers/chat-store/isChatLoading';
import { useChatStoreActions } from '../../../controllers/chat-store/useChatStoreActions';
import { useConversation } from '../../../controllers/conversation/useConversation';

export type ScreenType = 'loading' | 'empty' | 'conversation' | 'skills' | 'prompt-library';

type ScreenState = {
	screenType: ScreenType;
	openInlineBrowse: (screen: 'skills' | 'prompt-library') => void;
	closeInlineBrowse: () => void;
};

/**
 * Calculates screen type based on conversation state and manages inline browse state.
 * Automatically closes inline browse when conversation, agent, or message status changes.
 *
 * IMPORTANT: This hook calculates loading state based on chat status, while ChatHistory
 * has additional loading logic for when getChat() is called. This means there may be
 * one render cycle where this hook returns a different loading state than ChatHistory.
 * This is intentional and resolves quickly when chat status updates, so use with caution.
 */
export const useCalculatedScreenType = (conversationId: string): ScreenState => {
	di(
		useConversation,
		useChatStoreActions,
		useChatSignals,
		useChatMessages,
		isChatLoading,
		useConversationActionsStore,
	);
	const { conversation, selectedAgent } = useConversation({ conversationId });
	const [{ status: chatStatus }] = useChatSignals();
	const [conversationMessages] = useChatMessages({ conversationId });
	const [{ activeAction }] = useConversationActionsStore();

	const [inlineBrowseScreen, setInlineBrowseScreen] = useState<'skills' | 'prompt-library' | null>(
		null,
	);

	const prevConversationIdRef = useRef(conversationId);
	const prevAgentIdRef = useRef(selectedAgent?.id);
	const prevStatusRef = useRef(conversation?.status);

	// Auto-close inline browse when conversation, agent, or message status changes
	useEffect(() => {
		const conversationChanged = prevConversationIdRef.current !== conversationId;
		const agentChanged = prevAgentIdRef.current !== selectedAgent?.id;
		const messageSent =
			prevStatusRef.current !== conversation?.status &&
			conversation?.status === CHAT_STORE_STATUS.awaitingResponse;

		prevConversationIdRef.current = conversationId;
		prevAgentIdRef.current = selectedAgent?.id;
		prevStatusRef.current = conversation?.status;

		if ((conversationChanged || agentChanged || messageSent) && inlineBrowseScreen) {
			setInlineBrowseScreen(null);
		}
	}, [conversationId, selectedAgent?.id, conversation?.status, inlineBrowseScreen]);

	// Get the base screen type based on the conversation state
	const getBaseScreenType = useCallback((): 'loading' | 'empty' | 'conversation' => {
		const noConversation = !conversation || !conversationMessages.length;
		const isChatStoreOrConversationLoading = isChatLoading(chatStatus, conversation?.status);
		const isActiveActionInEmptyConversation =
			activeAction && conversationId === EMPTY_CONVERSATION_ID;

		if (noConversation && isChatStoreOrConversationLoading) {
			return 'loading';
		}
		if (noConversation) {
			return 'empty';
		}
		if (isActiveActionInEmptyConversation) {
			return 'empty';
		}
		return 'conversation';
	}, [conversation, conversationMessages.length, chatStatus, activeAction, conversationId]);

	const openInlineBrowse = useCallback((screen: 'skills' | 'prompt-library') => {
		setInlineBrowseScreen(screen);
	}, []);

	const closeInlineBrowse = useCallback(() => {
		setInlineBrowseScreen(null);

		// Return focus to chat input for accessibility
		setTimeout(() => {
			const chatTextarea = getDocument()?.querySelector(
				'#ak-editor-textarea',
			) as HTMLTextAreaElement;
			if (chatTextarea) {
				chatTextarea.focus();
			}
		}, 100);
	}, []);

	const screenType: ScreenType = inlineBrowseScreen ?? getBaseScreenType();

	return {
		screenType,
		openInlineBrowse,
		closeInlineBrowse,
	};
};
